/**
 * The chart's Drops lane: │ a planned drop · ⚡ one the optimizer adds.
 * Hovering a mark gives its exact time, on the country's clock and the
 * Danish one, and shows its hour on the card (user, 12/10/2026). The ticks
 * sit on whole screen pixels: a 2 px line at a fractional position is
 * smeared over three pixels by the browser, so ticks 30 min apart looked
 * like different widths and gaps ("why arent the same width?").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Zap } from 'lucide-react';

import { Tooltip } from '@/components/ui';

import { dkLabel } from '../../../../../../utils/clock/dk-label';
import { axisPct, minuteLabel } from '../../hour-chart-math';

interface Props {
  slotMinutes: number[];
  extras: Array<{ minute: number; kind: 'extra' | 'probe' }>;
  timezone: string;
  countryFlag: string;
  onHover: (hour: number) => void;
}

const MOVE = 'absolute top-0 transition-[left] duration-500 ease-out motion-reduce:transition-none';
const dayMinute = (m: number) => ((m % 1440) + 1440) % 1440;

/** The lane's width, kept current — 0 until measured (and in tests). */
function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

/** An invisible 12 px target centred on the mark — the tooltip and the hour on hover. */
const Hover: FC<{ left: string; tip: string; onEnter: () => void; children?: ReactNode }> = ({ left, tip, onEnter, children }) => (
  <div className={MOVE} style={{ left }} onMouseEnter={onEnter}>
    <Tooltip content={tip} contentClassName="min-w-0 whitespace-nowrap px-3 py-1.5" className="h-4 w-3 -translate-x-1/2 cursor-default items-start justify-center">
      {children ?? <span className="block h-4 w-3" />}
    </Tooltip>
  </div>
);

export const DropsLane: FC<Props> = ({ slotMinutes, extras, timezone, countryFlag, onHover }) => {
  const { ref, width } = useWidth();
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  // An even number of screen pixels wide, its centre on a screen pixel: both edges land on pixels.
  const tick = Math.max(2, 2 * Math.round(dpr)) / dpr;
  const centre = (m: number) => Math.round((axisPct(m) / 100) * width * dpr) / dpr;
  // Danish first (15/10/2026), the country's time after it.
  const when = (m: number) => `🇩🇰 ${dkLabel(m, timezone) ?? '—'} · ${countryFlag} ${minuteLabel(dayMinute(m))}${m >= 1440 ? ' (next day)' : ''}`;
  const hourOf = (m: number) => () => onHover(Math.floor(dayMinute(m) / 60));
  const drops = [...slotMinutes].sort((a, b) => a - b);
  return (
    <div ref={ref} className="absolute inset-0">
      {drops.map((m) => (
        <span key={`t${m}`} className={`${MOVE} h-2.5 rounded-full bg-foreground/70`} style={{ left: width > 0 ? centre(m) - tick / 2 : `calc(${axisPct(m)}% - 1px)`, width: tick }} />
      ))}
      {drops.map((m, i) => (
        <Hover key={`h${m}`} left={`${axisPct(m)}%`} tip={`Drop ${i + 1} of ${drops.length} · ${when(m)}`} onEnter={hourOf(m)} />
      ))}
      {extras.map((e) => (
        <Hover key={`e${e.minute}`} left={`${axisPct(e.minute)}%`} tip={`${e.kind === 'extra' ? 'Extra drop' : 'Test drop'} the optimizer adds · ${when(e.minute)}`} onEnter={hourOf(e.minute)}>
          <Zap className="h-3 w-3 fill-primary/30 text-primary" aria-label={e.kind === 'extra' ? 'extra drop' : 'test drop'} />
        </Hover>
      ))}
    </div>
  );
};
